import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useTranslation } from "react-i18next";
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsIcon from '@mui/icons-material/Settings';

import {
  AccountBoxIcon,
  AdminIcon,
  CrewIcon,
  HomeIcon,
} from '@src/shared/ui/icons';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebar:collapsed';

function getStoredSidebarCollapsedState() {
  const value = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);

  if (value === 'true') return true;
  if (value === 'false') return false;

  return true;
}

type SidebarLeafItem = {
  key: string;
  label: string;
  to: string;
  icon?: ReactElement;
  children?: never;
};

type SidebarGroupItem = {
  key: string;
  label: string;
  icon?: ReactElement;
  children: SidebarLeafItem[];
  to?: never;
};

export type SidebarItem = SidebarLeafItem | SidebarGroupItem;

type SidebarItems = {
  top: SidebarItem[];
  bottom: SidebarItem[];
};

type SidebarContextValue = {
  isCollapsed: boolean;
  isExpanded: boolean;
  open: () => void;
  collapse: () => void;
  toggle: () => void;
  setHovered: (value: boolean) => void;
  items: SidebarItems;
};

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

type SidebarProviderProps = {
  children: ReactNode;
};

function SidebarProvider({ children }: SidebarProviderProps) {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(getStoredSidebarCollapsedState);
  const [isHovered, setIsHovered] = useState(false);
  const ignoringHoverRef = useRef(false);

  const isExpanded = !isCollapsed || isHovered;

  const startIgnoringHover = useCallback(() => {
    ignoringHoverRef.current = true;
    setTimeout(() => { ignoringHoverRef.current = false; }, 400);
  }, []);

  const open = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
    setIsHovered(false);
    startIgnoringHover();
  }, [startIgnoringHover]);

  const toggle = useCallback(() => {
    setIsCollapsed((prevValue) => {
      if (!prevValue) {
        setIsHovered(false);
        startIgnoringHover();
      }
      return !prevValue;
    });
  }, [startIgnoringHover]);

  const setHovered = useCallback((value: boolean) => {
    if (value && ignoringHoverRef.current) return;
    setIsHovered(value);
  }, []);

  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (isCollapsed) {
      setIsHovered(false);
    }
  }, [pathname, isCollapsed]);

  useEffect(() => {
    window.localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(isCollapsed),
    );
  }, [isCollapsed]);

  const top = useMemo(
    () => [
      {
        key: 'dashboard',
        label: t('sidebar.home', 'Home'),
        to: '/dashboard',
        icon: <HomeIcon color="inherit" />,
      },
      {
        key: 'admin',
        label: t('sidebar.admin'),
        icon: <AdminIcon color="inherit" />,
        children: [
          {
            key: 'absence',
            label: t('sidebar.absence'),
            to: '/absence',
          },
          {
            key: 'users',
            label: t('sidebar.user_management', 'User management'),
            to: '/users',
          },
        ],
      },
      {
        key: 'clients',
        label: t('sidebar.clients'),
        to: '/clients',
        icon: <AccountBoxIcon color="inherit" />,
      },
      {
        key: 'team',
        label: t('sidebar.team'),
        to: '/team',
        icon: <CrewIcon color="inherit" />,
      },
    ],
    [t],
  );

  const bottom = useMemo(
    () => [
      {
        key: 'settings',
        label: t('sidebar.settings'),
        to: '/settings',
        icon: <SettingsIcon color="inherit" />,
      },
      {
        key: 'logout',
        label: t('sidebar.logout'),
        to: '/auth/logout',
        icon: <LogoutOutlinedIcon color="inherit" />,
      },
    ],
    [t],
  );

  const contextValue = useMemo(
    () => ({
      isCollapsed,
      isExpanded,
      open,
      collapse,
      toggle,
      setHovered,
      items: {
        top,
        bottom,
      },
    }),
    [bottom, collapse, isCollapsed, isExpanded, open, setHovered, toggle, top],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
}

SidebarProvider.displayName = 'SidebarProvider';
export { SidebarContext, SidebarProvider };
